/**
 * fieldRefData.js — the field zone's durable dropdown vocabulary.
 *
 * The enum vocabularies the capture modal renders (FIELD_REF_KEYS) are
 * fetched unscoped and parked in the tmg-field `refdata` store (fieldDb v2),
 * so an offline cold launch still has real pulldowns instead of empty ones.
 * This module OWNS the key list — nothing else declares it.
 *
 * GLOBAL-ONLY, AND THAT IS A LOAD-BEARING ASSUMPTION (D-43). The fetch below
 * sends NO site_id and NO caller_email, so it receives the global vocabulary
 * and nothing else. Witnessed 2026-08-25 against tmg-app-db-test: each of the
 * four keys resolves to exactly one `refdata-<field>-global` document, and
 * ZERO documents for these four fields carry a defined, non-null site_id. If a
 * site-scoped value is ever added for one of them, THE FIELD ZONE WILL NOT SEE
 * IT — the sheet renders the global list only, with no error and no visible
 * tell that anything is missing. `oem` (added in OEM arc Phase 2) holds the
 * same way: the 2026-10-07 audit found one `refdata-oem-global` document in
 * each environment and no per-site oem document — and OEM is a fixed global
 * list by decision, so this module reading global-only is the rule, not a gap.
 *
 * src/shared/hooks/useReferenceData.js remains the app-wide hook (session
 * memory, 5-minute TTL, site-aware) and is untouched by this module; the field
 * zone deliberately does not use it, because a module-level Map dies with the
 * tab and cannot serve a cold launch.
 */

import { useEffect, useState } from 'react';
import { apiFetch } from '../../../auth/apiFetch';
import { readRefData, mergeRefData } from '../db/fieldStore';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071/api';

/**
 * The enum vocabularies the field capture modal renders. Single source of truth.
 * `oem` joined in OEM arc Phase 2. Adding a field here no longer moves the
 * cache key (FIELD_REFDATA_KEY is a literal), and a device whose row predates
 * the new field fetches it on its next read — see useFieldReferenceData.
 */
export const FIELD_REF_KEYS = ['tool_type', 'material', 'weight_unit', 'part_number_type', 'oem'];

// The ONE row this module reads and writes. A FIXED LITERAL, ON PURPOSE.
// Never compute it from FIELD_REF_KEYS or any list. It used to be derived
// from the list, so adding a field changed the key; every device's only row
// went unread, and an offline device showed EMPTY pickers for every enum at
// once, because they all share this row. The fields a row covers now live
// INSIDE it (`fields`). Changing this string re-creates that outage everywhere.
export const FIELD_REFDATA_KEY = 'field-refdata';

// The only value the old derived key ever had (FIELD_REF_KEYS was these four
// from 27cec78 until Phase 2 added oem). A LITERAL, never recomputed: computing
// it from the longer list names a row no device holds. Read to migrate; never
// written or deleted.
export const LEGACY_FIELD_REFDATA_KEY = 'material,part_number_type,tool_type,weight_unit:global';

/**
 * Fetch every FIELD_REF_KEYS vocabulary and merge it into the refdata store.
 *
 * NEVER throws and never rejects — every failure path logs and returns, so a
 * caller can fire this without a guard. It also never clears the existing row:
 * a failed refresh leaves the last good vocabulary in place, which is the
 * entire point of a durable cache.
 */
export async function hydrateFieldRefData() {
  try {
    const params = new URLSearchParams({ fields: FIELD_REF_KEYS.join(',') });
    const res = await apiFetch(`${API_BASE_URL}/get_reference_data?${params}`);
    if (!res.ok) {
      console.error('hydrateFieldRefData: get_reference_data failed:', res.status);
      return;
    }
    const data = await res.json();
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      console.error('hydrateFieldRefData: unexpected response shape, cache left as it was');
      return;
    }
    // MERGE, never replace.
    await mergeRefData(FIELD_REFDATA_KEY, LEGACY_FIELD_REFDATA_KEY, data, FIELD_REF_KEYS);
  } catch (err) {
    console.error('hydrateFieldRefData failed:', err);
  }
}

/**
 * The FIELD_REF_KEYS a stored row does not hold a list for. Empty = the row
 * covers every requested field. Reads the row's own `fields` record (written
 * by mergeRefData and the legacy migration); a field the row lacks is one the
 * read hook must fetch.
 */
export function missingRefFields(fields) {
  const held = Array.isArray(fields) ? fields : [];
  return FIELD_REF_KEYS.filter((k) => !held.includes(k));
}

/**
 * The field zone's read side: the durable row, with a hydrate when the store
 * has no row (a device that reached the modal before any Load completed) OR
 * when the row lacks a requested field (Phase 2: a device whose row predates
 * `oem` holds four lists and would otherwise show an empty OEM picker until its
 * next online Load, with no self-heal).
 *
 * A row that exists is SERVED FIRST, before any fetch, so the lists it holds
 * show at once and stay shown if the fetch fails — offline, a failed hydrate
 * never clears anything. The row is re-read after the hydrate, so a successful
 * fetch lands without a remount.
 *
 * Still open, by design: this reads once per mount, and the Load hydrate
 * (useFieldLoad) is not awaited, so a record screen already open when THAT
 * merge lands keeps its earlier lists until it remounts. This hook's own
 * subset hydrate narrows the window; it does not close it.
 *
 * Options default to {} on every path so `options[key] || []` stays safe while
 * loading, after a failure, and on an empty store alike.
 *
 * Unmount guard is the `alive` closure + cleanup pattern from useFieldLoad.js.
 *
 * @returns {{ options: Object, loading: boolean }}
 */
export function useFieldReferenceData() {
  const [options, setOptions] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;

    async function read() {
      try {
        let stored = await readRefData(FIELD_REFDATA_KEY, LEGACY_FIELD_REFDATA_KEY);
        if (stored && alive) setOptions(stored.options);
        if (stored === null || missingRefFields(stored.fields).length > 0) {
          await hydrateFieldRefData();
          const fresh = await readRefData(FIELD_REFDATA_KEY, LEGACY_FIELD_REFDATA_KEY);
          if (fresh) stored = fresh;
        }
        if (alive) setOptions(stored ? stored.options : {});
      } catch (err) {
        console.error('useFieldReferenceData: read failed', err);
      } finally {
        if (alive) setLoading(false);
      }
    }

    read();
    return () => { alive = false; };
  }, []);

  return { options, loading };
}
